import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  deleteWatchlistItem,
  fetchWatchlistItem,
  promoteWatchlistToInvestment,
  refreshWatchlistQuote,
  updateWatchlistItem,
} from "../../lib/watchlist";
import { OtterfolioLayout } from "./OtterfolioLayout";
import { RenditebrueckeCard } from "./RenditebrueckeCard";
import { OTTERFOLIO_THEME } from "./theme";

export function WatchlistItemDetail() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["watchlist", id],
    queryFn: () => fetchWatchlistItem(id),
    enabled: !!id,
  });

  const refreshQuoteMut = useMutation({
    mutationFn: () => refreshWatchlistQuote(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watchlist", id] }),
  });
  const promoteMut = useMutation({
    mutationFn: () => promoteWatchlistToInvestment(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["otterfolio", "overview"] });
      nav(`/investments/${r.investmentId}`);
    },
  });

  if (isLoading) {
    return (
      <OtterfolioLayout>
        <div style={{ padding: 40, textAlign: "center", opacity: 0.7 }}>
          {t("otterfolio.loading", { defaultValue: "Laden…" })}
        </div>
      </OtterfolioLayout>
    );
  }
  if (isError || !data) {
    return (
      <OtterfolioLayout>
        <div style={{ padding: 40, textAlign: "center", opacity: 0.7 }}>
          {t("otterfolio.loadError", { defaultValue: "Konnte nicht geladen werden." })}
        </div>
      </OtterfolioLayout>
    );
  }

  const chartData = data.quotes.map((q) => ({
    date: q.asOf.slice(0, 10),
    price: q.priceCents / 100,
  }));

  return (
    <OtterfolioLayout>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Link to="/otterfolio" style={{ color: OTTERFOLIO_THEME.primary, textDecoration: "none" }}>
          {t("otterfolio.watchlist.detailBackLink", { defaultValue: "← Zurück zum Otterfolio" })}
        </Link>

        <header>
          <h1 style={{ margin: 0, fontSize: 22, color: OTTERFOLIO_THEME.text }}>
            {data.symbol} — {data.name}
          </h1>
          {data.investmentCategory && (
            <div style={{ fontSize: 12, color: OTTERFOLIO_THEME.textMuted, marginTop: 4 }}>
              {data.investmentCategory === "FELS" ? "🪨 Fels" : "🚀 Rakete"}
            </div>
          )}
        </header>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => refreshQuoteMut.mutate()}
            disabled={refreshQuoteMut.isPending}
          >
            {t("otterfolio.watchlist.refreshQuote", { defaultValue: "Kurs aktualisieren" })}
          </button>
          <button type="button" onClick={() => promoteMut.mutate()} disabled={promoteMut.isPending}>
            {t("otterfolio.watchlist.promoteButton", {
              defaultValue: "🛒 Zum Portfolio hinzufügen",
            })}
          </button>
          <button
            type="button"
            onClick={async () => {
              if (
                !confirm(
                  t("otterfolio.watchlist.confirmDelete", {
                    defaultValue: "Wirklich von der Watchlist entfernen?",
                  }),
                )
              )
                return;
              await deleteWatchlistItem(id);
              qc.invalidateQueries({ queryKey: ["otterfolio", "overview"] });
              nav("/otterfolio");
            }}
          >
            Löschen
          </button>
        </div>

        <RenditebrueckeCard
          rb={data.renditebruecke}
          overrides={{
            growthEstimatePercent: data.growthEstimatePercent,
            payoutYieldPercent: data.payoutYieldPercent,
            valuationDeltaPercent: data.valuationDeltaPercent,
          }}
          renditebrueckeUpdatedAt={data.renditebrueckeUpdatedAt}
          onSave={async (v) => {
            await updateWatchlistItem(id, v);
            qc.invalidateQueries({ queryKey: ["watchlist", id] });
            qc.invalidateQueries({ queryKey: ["otterfolio", "overview"] });
          }}
        />

        {chartData.length > 0 ? (
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke={OTTERFOLIO_THEME.primary}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: OTTERFOLIO_THEME.textMuted,
              fontSize: 13,
            }}
          >
            Noch keine Kurshistorie. Nutz „Kurs aktualisieren" um den ersten Wert zu laden.
          </div>
        )}

        {data.notes && (
          <div
            style={{
              padding: 12,
              background: OTTERFOLIO_THEME.cardBg,
              border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
              borderRadius: 8,
              fontSize: 13,
              color: OTTERFOLIO_THEME.text,
            }}
          >
            {data.notes}
          </div>
        )}
      </div>
    </OtterfolioLayout>
  );
}
